import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Space, Divider, Tag } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const { Title, Text } = Typography;

interface LoginRequest {
  email: string;
  password: string;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onFinish = async (values: LoginRequest) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || 'Invalid credentials');
        return;
      }

      // Persist auth state
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_user', JSON.stringify(data.data));

      navigate('/dashboard');
    } catch (err: any) {
      setError('Cannot reach the server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background" />
      <div className="login-content">
        <Card className="login-card">
          <div className="login-header">
            <img
              src="/logo.png"
              alt="Adam's Palm Resort"
              className="login-logo"
            />
            <Title level={2} className="login-title">
              Admin Panel
            </Title>
            <Text type="secondary" className="login-subtitle">
              Sign in to your account
            </Text>
          </div>

          <Divider />

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Form
            name="login"
            onFinish={onFinish}
            layout="vertical"
            size="large"
            autoComplete="off"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter your email" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' },
                { min: 6, message: 'Password must be at least 6 characters!' },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{ height: '48px', fontSize: '16px' }}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <div className="login-footer">
          <Text type="secondary" style={{ fontSize: '12px' }}>
            © 2026 Adam's Palm Resort. All rights reserved.
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Luxury Resort Management System
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Login;